import { z } from 'zod';
import { EmailSchema, LangCodeSchema, SlugSchema, TimestampSchema, UuidSchema } from './primitives';

export const RESERVED_TENANT_SLUGS = [
  'admin',
  'api',
  'app-api',
  'www',
  'support',
  'blog',
  'help',
  'status',
] as const;

export type ReservedTenantSlug = (typeof RESERVED_TENANT_SLUGS)[number];

const reservedTenantSlugSet = new Set<string>(RESERVED_TENANT_SLUGS);

export const BetaSignupTenantSlugSchema = SlugSchema.min(3)
  .max(30)
  .refine((slug) => !reservedTenantSlugSet.has(slug), {
    message: 'reserved_slug',
  });

export type BetaSignupTenantSlug = z.infer<typeof BetaSignupTenantSlugSchema>;

export const SubscriptionPlanSchema = z.enum(['free', 'pro', 'enterprise']);
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;

export const SubscriptionStatusSchema = z.enum(['active', 'trialing', 'past_due', 'canceled']);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

export const SubscriptionSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  plan: SubscriptionPlanSchema,
  status: SubscriptionStatusSchema,
  current_period_end: TimestampSchema.nullable(),
  stripe_customer_id: z.string().nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Subscription = z.infer<typeof SubscriptionSchema>;

export const InvitationRoleSchema = z.enum(['owner', 'admin', 'member']);
export type InvitationRole = z.infer<typeof InvitationRoleSchema>;

export const InvitationSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  email: EmailSchema,
  role: InvitationRoleSchema,
  token_hash: z.string().regex(/^[a-f0-9]{64}$/),
  token_prefix: z.string().nullable(),
  invited_by: UuidSchema.nullable(),
  expires_at: TimestampSchema,
  accepted_at: TimestampSchema.nullable(),
  revoked_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Invitation = z.infer<typeof InvitationSchema>;

export const InvitationPublicSchema = InvitationSchema.omit({
  token_hash: true,
});
export type InvitationPublic = z.infer<typeof InvitationPublicSchema>;

export const InvitationTokenSchema = z
  .string()
  .min(32)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/);
export type InvitationToken = z.infer<typeof InvitationTokenSchema>;

export const InvitationCreateInputSchema = z.object({
  email: EmailSchema.transform((email) => email.toLowerCase()),
  role: InvitationRoleSchema.default('member'),
  expires_at: TimestampSchema.optional(),
});
export type InvitationCreateInput = z.input<typeof InvitationCreateInputSchema>;

export const InvitationCreateResponseSchema = z.object({
  invitation: InvitationPublicSchema,
  token: InvitationTokenSchema,
});
export type InvitationCreateResponse = z.infer<typeof InvitationCreateResponseSchema>;

export const InvitationListResponseSchema = z.object({
  data: z.array(InvitationPublicSchema),
});
export type InvitationListResponse = z.infer<typeof InvitationListResponseSchema>;

export const InvitationRevokeInputSchema = z.object({
  id: UuidSchema,
});
export type InvitationRevokeInput = z.infer<typeof InvitationRevokeInputSchema>;

export const InvitationRevokeResponseSchema = z.object({
  invitation: InvitationPublicSchema,
});
export type InvitationRevokeResponse = z.infer<typeof InvitationRevokeResponseSchema>;

export const InvitationAcceptInputSchema = z.object({
  token: InvitationTokenSchema,
});
export type InvitationAcceptInput = z.infer<typeof InvitationAcceptInputSchema>;

export const InvitationAcceptResponseSchema = z.object({
  invitation: InvitationPublicSchema,
  tenant: z.object({
    id: UuidSchema,
    slug: SlugSchema,
    name: z.string().min(1).max(120),
  }),
  membership: z.object({
    tenant_id: UuidSchema,
    user_id: UuidSchema,
    role: InvitationRoleSchema,
  }),
  redirect_to: z.string().min(1),
});
export type InvitationAcceptResponse = z.infer<typeof InvitationAcceptResponseSchema>;

export const BetaFeedbackStatusSchema = z.enum(['open', 'triaged', 'resolved', 'closed']);
export type BetaFeedbackStatus = z.infer<typeof BetaFeedbackStatusSchema>;

const FeedbackStoragePathSchema = z
  .string()
  .trim()
  .min(1)
  .max(512)
  .regex(/^[^:/?#]+\/[^:/?#]+\/[^:/?#]+$/)
  .refine((path) => !path.includes('..'), {
    message: 'invalid_storage_path',
  });

const HttpUrlSchema = z
  .string()
  .trim()
  .max(2048)
  .url()
  .refine(
    (value) => {
      try {
        const url = new URL(value);
        return url.protocol === 'https:' || url.protocol === 'http:';
      } catch {
        return false;
      }
    },
    {
      message: 'invalid_http_url',
    },
  );

export const BetaFeedbackSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  user_id: UuidSchema,
  page_url: HttpUrlSchema.nullable(),
  message: z.string().min(1).max(4000),
  screenshot_path: FeedbackStoragePathSchema.nullable(),
  status: BetaFeedbackStatusSchema,
  resolved_at: TimestampSchema.nullable(),
  resolved_by: UuidSchema.nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type BetaFeedback = z.infer<typeof BetaFeedbackSchema>;

export const BetaFeedbackCreateInputSchema = z.object({
  message: z.string().trim().min(1).max(4000),
  page_url: HttpUrlSchema.nullable().optional(),
  screenshot_path: FeedbackStoragePathSchema.nullable().optional(),
});
export type BetaFeedbackCreateInput = z.input<typeof BetaFeedbackCreateInputSchema>;

export const BetaFeedbackCreateResponseSchema = z.object({
  feedback: BetaFeedbackSchema,
});
export type BetaFeedbackCreateResponse = z.infer<typeof BetaFeedbackCreateResponseSchema>;

export const BetaFeedbackListResponseSchema = z.object({
  data: z.array(BetaFeedbackSchema),
});
export type BetaFeedbackListResponse = z.infer<typeof BetaFeedbackListResponseSchema>;

export const BetaFeedbackUpdateInputSchema = z.object({
  id: UuidSchema,
  status: BetaFeedbackStatusSchema,
});
export type BetaFeedbackUpdateInput = z.infer<typeof BetaFeedbackUpdateInputSchema>;

export const BetaFeedbackUpdateResponseSchema = z.object({
  feedback: BetaFeedbackSchema,
});
export type BetaFeedbackUpdateResponse = z.infer<typeof BetaFeedbackUpdateResponseSchema>;

export const BetaSignupInputSchema = z.object({
  email: EmailSchema.transform((email) => email.toLowerCase()),
  password: z.string().min(8).max(128),
  display_name: z.string().trim().min(1).max(120),
  tenant_name: z.string().trim().min(1).max(120),
  tenant_slug: BetaSignupTenantSlugSchema,
  preferred_lang: LangCodeSchema,
});
export type BetaSignupInput = z.infer<typeof BetaSignupInputSchema>;

export const BetaSignupResponseSchema = z.object({
  user: z.object({
    id: UuidSchema,
    email: EmailSchema,
  }),
  tenant: z.object({
    id: UuidSchema,
    slug: BetaSignupTenantSlugSchema,
    name: z.string().min(1).max(120),
  }),
  redirect_to: z.string().min(1),
});
export type BetaSignupResponse = z.infer<typeof BetaSignupResponseSchema>;
